import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Loader2, Gift, Sparkles, Mail, CheckCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

// Stripe price IDs from environment variables
const STRIPE_PRICES: Record<string, string | undefined> = {
  lifetime: import.meta.env.VITE_STRIPE_LIFETIME_PRICE_ID,
  pro: import.meta.env.VITE_STRIPE_PRO_PRICE_ID,
};

export default function Signup() {
  const [, setLocation] = useLocation();
  const { signUp } = useAuth();
  const [businessName, setBusinessName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  
  // AppSumo integration
  const [appsumoLicense, setAppsumoLicense] = useState<string | null>(null);
  const [appsumoTier, setAppsumoTier] = useState<number | null>(null);
  
  // Plan intent for paid plan signup flow
  const [selectedPlan, setSelectedPlan] = useState<"lifetime" | "pro" | null>(null);
  
  // Email verification state
  const [verificationSent, setVerificationSent] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const license = params.get("appsumo");
    const tier = params.get("tier");
    const plan = params.get("plan");
    
    if (license) setAppsumoLicense(license);
    if (tier) setAppsumoTier(parseInt(tier) || 1);
    if (plan === "lifetime" || plan === "pro") setSelectedPlan(plan);
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setIsLoading(true);

    const result = await signUp(email, password, businessName);

    if (result.error) {
      setError(result.error);
      setIsLoading(false);
      return;
    }

    // If email verification is required, show the verification message
    if (result.requiresEmailVerification) {
      setVerificationSent(true);
      setIsLoading(false);
      return;
    }

    // If this is an AppSumo signup, link the license
    if (appsumoLicense) {
      try {
        const res = await api.post("/api/appsumo/link", { licenseUuid: appsumoLicense });
        if (res.ok) {
          setLocation("/dashboard?appsumo=activated");
          return;
        } else {
          // License linking failed, but account was created
          // Redirect to dashboard with warning
          setLocation("/dashboard?appsumo=link_failed");
          return;
        }
      } catch {
        // License linking failed, but account was created
        setLocation("/dashboard?appsumo=link_failed");
        return;
      }
    }

    // If user came from pricing page with a plan intent, trigger checkout
    if (selectedPlan) {
      const priceId = STRIPE_PRICES[selectedPlan];
      if (priceId) {
        try {
          const res = await api.post("/api/stripe/create-checkout-session", {
            priceId,
            planType: selectedPlan,
          });
          if (res.ok) {
            const data = await res.json();
            if (data.url) {
              window.location.href = data.url;
              return;
            }
          }
        } catch (err) {
          // If checkout fails, still redirect to dashboard
          console.error("Failed to start checkout:", err);
        }
      }
    }

    setLocation("/dashboard");
  }

  // Show verification sent screen
  if (verificationSent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Link href="/" className="flex flex-col items-center gap-2">
              <img src="/logo.png" alt="ReviewKit" className="h-12 w-auto object-contain" />
              <span className="font-bold text-xl text-muted-foreground">ReviewKit</span>
            </Link>
          </CardHeader>
          <CardContent className="text-center space-y-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 mx-auto">
              <Mail className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-2">Check your email</h2>
              <p className="text-muted-foreground">
                We've sent a verification link to <strong>{email}</strong>
              </p>
            </div>
            <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
              <p className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-600" />
                <span>Click the link in your email to verify your account and start using ReviewKit.</span>
              </p>
            </div>
            <div className="text-sm text-muted-foreground">
              <p>Didn't receive the email? Check your spam folder or</p>
              <Button
                variant="link"
                className="p-0 h-auto text-primary"
                onClick={() => {
                  setVerificationSent(false);
                  setError("");
                }}
              >
                try signing up again
              </Button>
            </div>
          </CardContent>
          <CardFooter className="justify-center">
            <p className="text-sm text-muted-foreground">
              Already verified?{" "}
              <Link href="/login" className="text-primary hover:underline">
                Sign in
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Link href="/" className="flex flex-col items-center gap-2">
            <img src="/logo.png" alt="ReviewKit" className="h-12 w-auto object-contain" />
            <span className="font-bold text-xl text-muted-foreground">ReviewKit</span>
          </Link>
          <CardTitle className="mt-4">Create your account</CardTitle>
          <CardDescription>
            {appsumoLicense 
              ? "Complete your AppSumo activation"
              : selectedPlan
              ? `Sign up to continue to ${selectedPlan === "lifetime" ? "Lifetime" : "Pro"} checkout`
              : "Start collecting reviews in minutes"}
          </CardDescription>
          
          {/* Show AppSumo badge if coming from AppSumo */}
          {appsumoLicense && (
            <div className="flex justify-center mt-2">
              <Badge variant="secondary" className="flex items-center gap-1">
                <Gift className="w-3 h-3" />
                AppSumo Tier {appsumoTier || 1}
              </Badge>
            </div>
          )}
          
          {/* Show plan badge if signing up with plan intent */}
          {selectedPlan && !appsumoLicense && (
            <div className="flex justify-center mt-2">
              <Badge variant="default" className="flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                {selectedPlan === "lifetime" ? "Lifetime Plan - $79" : "Pro Plan - $29/mo"}
              </Badge>
            </div>
          )}
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="businessName">Business name</Label>
              <Input
                id="businessName"
                type="text"
                placeholder="Acme HVAC Services"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
              <p className="text-xs text-muted-foreground">Must be at least 8 characters</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {appsumoLicense 
                    ? "Activating..." 
                    : selectedPlan 
                    ? "Creating account..." 
                    : "Creating account..."}
                </>
              ) : (
                appsumoLicense 
                  ? "Create Account & Activate" 
                  : selectedPlan
                  ? "Create Account & Continue to Checkout"
                  : "Create account"
              )}
            </Button>
            <p className="text-sm text-muted-foreground text-center">
              Already have an account?{" "}
              <Link 
                href={`/login${appsumoLicense ? `?appsumo=${appsumoLicense}&tier=${appsumoTier}` : ""}`} 
                className="text-primary hover:underline"
              >
                Sign in
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
